import { useQuery } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * The audit trail (spec §49).
 *
 * Read-only, because the trail is append-only on the server: there is no
 * endpoint to edit or delete an entry and there will not be one.
 */

export type AuditAction =
  | 'LOGIN'
  | 'LOGOUT'
  | 'LOGIN_FAILED'
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'APPROVE'
  | 'REJECT'
  | 'SUSPEND'
  | 'BLOCK'
  | 'VERIFY'
  | 'STATUS_CHANGE'
  | 'PRICE_CHANGE'
  | 'STOCK_CHANGE'
  | 'PAYMENT'
  | 'REFUND'
  | 'DEPOSIT_HOLD'
  | 'DEPOSIT_RELEASE'
  | 'SETTINGS_CHANGE'
  | 'ROLE_CHANGE'
  | 'EXPORT';

export interface AuditChange {
  field: string;
  from: unknown;
  to: unknown;
}

export interface AuditEntry {
  id: string;
  action: AuditAction;
  entity: string;
  entityId: string | null;
  actor: { id: string; email: string | null; role: string | null } | null;
  /** What the writer said, or a reconstruction from the fields. */
  summary: string;
  /** Only the fields that differ between the two snapshots. */
  changes: AuditChange[];
  oldValue: Record<string, unknown> | null;
  newValue: Record<string, unknown> | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

export interface AuditFilters {
  actions: Array<{ value: AuditAction; count: number }>;
  entities: Array<{ value: string; count: number }>;
  actors: Array<{ id: string; email: string | null; count: number }>;
}

export interface AuditParams {
  page?: number;
  limit?: number;
  search?: string;
  action?: AuditAction;
  entity?: string;
  actorId?: string;
  from?: string;
  to?: string;
}

const keys = {
  all: ['admin', 'audit-logs'] as const,
  list: (params: AuditParams) => [...keys.all, 'list', params] as const,
  filters: () => [...keys.all, 'filters'] as const,
};

export function useAuditLog(params: AuditParams) {
  return useQuery({
    queryKey: keys.list(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<AuditEntry>>('/admin/audit-logs', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          action: params.action,
          entity: params.entity,
          actorId: params.actorId,
          from: params.from || undefined,
          to: params.to || undefined,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

/**
 * The values that actually appear in the trail.
 *
 * Cached longer than the list: the set of entities an installation audits
 * changes when the code changes, not while somebody is reading the page.
 */
export function useAuditFilters() {
  return useQuery({
    queryKey: keys.filters(),
    queryFn: () => apiClient.get<AuditFilters>('/admin/audit-logs/filters'),
    staleTime: 5 * 60_000,
  });
}

/** Actions that describe something being taken away, for tone. */
export const DESTRUCTIVE_ACTIONS: AuditAction[] = [
  'DELETE',
  'REJECT',
  'SUSPEND',
  'BLOCK',
  'LOGIN_FAILED',
];
